import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { download } from '../../api';
import { useCan } from '../../auth';
import { MutationError, Notice, Section } from '../../ui/kit';

/**
 * Support diagnostics (docs/operations/diagnostics.md): a JSON file an administrator downloads and
 * sends to Ward support. Owner / Security Admin only (`audit:read`); every download is audited.
 */
const DIAGNOSTICS_PERMISSION = 'audit:read';

export function diagnosticsExportPath(includeDeviceIds: boolean): string {
  return `/diagnostics/export${includeDeviceIds ? '?includeDeviceIds=true' : ''}`;
}

export function deviceDiagnosticsPath(deviceId: string): string {
  return `/diagnostics/devices/${encodeURIComponent(deviceId)}`;
}

const DIAGNOSTICS_INCLUDED =
  'Included: server version, non-secret configuration flags, database migration level, policy and protection bundle state, device counts (by extension version, status, OS and browser, heartbeat age), enrollment token metadata, error codes and counts, and integration status.';
const DIAGNOSTICS_EXCLUDED =
  'Never included: browsing activity, event contents, URLs, file names, email addresses, passwords, tokens, keys or other secrets.';

export function DiagnosticsPanel() {
  const can = useCan();
  const [includeDeviceIds, setIncludeDeviceIds] = useState(false);
  const dl = useMutation({ mutationFn: () => download(diagnosticsExportPath(includeDeviceIds), undefined, 'ward-diagnostics.json') });
  if (!can(DIAGNOSTICS_PERMISSION)) return null;
  return (
    <Section title="Support diagnostics" aside="for Ward support">
      <p className="sub mt-12">
        Download a diagnostics file to attach to a support request. {DIAGNOSTICS_INCLUDED} {DIAGNOSTICS_EXCLUDED}
      </p>
      <label className="check mt-8">
        <input type="checkbox" checked={includeDeviceIds} onChange={(e) => setIncludeDeviceIds(e.target.checked)} /> Include device identifiers (device IDs and names)
      </label>
      <p className="sub">Off by default. Only needed when support asks about specific devices. Email addresses are never included.</p>
      <MutationError error={dl.error} />
      {dl.isSuccess && <Notice tone="allow" role="status">Diagnostics downloaded. This download is recorded in the audit log.</Notice>}
      <button className="btn mt-12" disabled={dl.isPending} onClick={() => dl.mutate()}>
        {dl.isPending ? 'Preparing…' : 'Download diagnostics'}
      </button>
    </Section>
  );
}

/** Per-device diagnostics action for the device detail view. */
export function DeviceDiagnosticsButton({ deviceId }: { deviceId: string }) {
  const can = useCan();
  const dl = useMutation({ mutationFn: () => download(deviceDiagnosticsPath(deviceId), undefined, `ward-device-diagnostics-${deviceId.slice(0, 8)}.json`) });
  if (!can(DIAGNOSTICS_PERMISSION)) return null;
  return (
    <div className="mt-12">
      <button
        className="btn small"
        disabled={dl.isPending}
        title="Versions, status, policy state and error codes for this device. No browsing activity, event contents, URLs or email addresses."
        onClick={() => dl.mutate()}
      >
        {dl.isPending ? 'Preparing…' : 'Diagnostics'}
      </button>
      <MutationError error={dl.error} />
    </div>
  );
}
